/**
 * Client-side portfolio stats compute used by RebalanceSimulator (interactive
 * sliders) and PortfolioSnapshot (equal-weight ↔ portfolio-weight toggle).
 * Same math as the snapshot script, just runs in the browser using the
 * per-holding μ/σ/β/composite and cov matrix shipped in the snapshot JSON.
 */

export interface PerHolding {
  symbol: string;
  sector: string;
  composite: number;
  mu: number;
  sigmaAnnual: number;
  betaSpy: number | null;
  currentWeight: number;
}

export interface CovMatrix {
  symbols: string[];
  matrix: number[][];
}

export interface FeesSpec {
  mgmtFee: number;
  perfFee: number;
  hurdle: number;
}

export interface PortfolioStats {
  composite: number;
  expectedReturn: number;
  expectedStdev: number;
  sharpe: number;
  beta: number;
  sectorWeights: Map<string, number>;
  hhi: number;
  netReturn: number;
  netSharpe: number;
}

/**
 * Compute portfolio stats for an arbitrary stock-bucket weight vector.
 * `weights` and `holdings` MUST be in the same order as `cov.symbols`.
 * Stock weights are renormalized to sum to (1 - cashWeight) before the math.
 */
export function computePortfolioStats(
  holdings: PerHolding[],
  weights: number[],
  cov: number[][],
  cashWeight: number,
  fees: FeesSpec,
): PortfolioStats {
  const stockTotal = weights.reduce((s, x) => s + x, 0);
  if (stockTotal <= 0) {
    return {
      composite: 0, expectedReturn: 0, expectedStdev: 0, sharpe: 0, beta: 0,
      sectorWeights: new Map(), hhi: 0, netReturn: 0, netSharpe: 0,
    };
  }
  const stockScale = 1 - cashWeight;
  const wNorm = weights.map(w => (w / stockTotal) * stockScale);
  const composite = wNorm.reduce((s, w, i) => s + w * holdings[i].composite, 0) / Math.max(stockScale, 1e-9);
  const muStock = wNorm.reduce((s, w, i) => s + w * holdings[i].mu, 0);
  const beta = wNorm.reduce((s, w, i) => s + w * (holdings[i].betaSpy ?? 1.0), 0);

  let varP = 0;
  for (let i = 0; i < wNorm.length; i++) {
    for (let j = 0; j < wNorm.length; j++) {
      varP += wNorm[i] * wNorm[j] * cov[i][j];
    }
  }
  const sigma = Math.sqrt(Math.max(0, varP));
  const sharpe = sigma > 0 ? muStock / sigma : 0;

  const afterMgmt = muStock - fees.mgmtFee;
  const perfBase = Math.max(0, afterMgmt - fees.hurdle);
  const netReturn = afterMgmt - perfBase * fees.perfFee;
  const netSharpe = sigma > 0 ? netReturn / sigma : 0;

  const sectorWeights = new Map<string, number>();
  for (let i = 0; i < holdings.length; i++) {
    sectorWeights.set(holdings[i].sector, (sectorWeights.get(holdings[i].sector) ?? 0) + wNorm[i]);
  }
  let hhi = 0;
  for (const w of sectorWeights.values()) hhi += w * w;

  return { composite, expectedReturn: muStock, expectedStdev: sigma, sharpe, beta, sectorWeights, hhi, netReturn, netSharpe };
}

/**
 * Build a weight vector aligned to `cov.symbols` from the per-holding data.
 * `mode` selects portfolio (currentWeight) or equal-weight (1/N).
 */
export function buildWeightVector(
  perHolding: PerHolding[],
  cov: CovMatrix,
  mode: 'portfolio' | 'equal',
): { holdingsByOrder: PerHolding[]; weights: number[] } {
  const order = cov.symbols;
  const holdingsByOrder = order
    .map(sym => perHolding.find(h => h.symbol === sym)!)
    .filter(Boolean) as PerHolding[];

  let weights: number[];
  if (mode === 'equal') {
    const n = holdingsByOrder.length;
    weights = holdingsByOrder.map(() => 1 / Math.max(n, 1));
  } else {
    weights = holdingsByOrder.map(h => h.currentWeight);
    const sum = weights.reduce((s, x) => s + x, 0);
    if (sum > 0) weights = weights.map(w => w / sum);
  }
  return { holdingsByOrder, weights };
}
